import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	CardBody,
	FormField,
	Input,
	Modal,
	Page,
	Select,
	Textarea,
	useToast,
} from "@theg1group/ui";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API_BASE } from "../config.ts";
import { api } from "../lib/api.ts";
import { fmtAgo, fmtDate, severityTone } from "../lib/format.ts";
import type { CaseDetailData, CaseItem } from "../lib/types.ts";

const STATUSES = ["open", "in_review", "closed"];
const FINDING_KINDS = [
	"overlap",
	"sanctioned_exposure",
	"mixer_exposure",
	"fraud_pattern",
	"note",
	"other",
];
const SEVERITIES = ["low", "medium", "high", "critical"];
const ITEM_TYPES = ["address", "partner", "transfer", "trace", "label"];

export function CaseDetail() {
	const { id = "" } = useParams();
	const qc = useQueryClient();
	const toast = useToast();
	const [notes, setNotes] = useState<string | null>(null);
	const [showItem, setShowItem] = useState(false);
	const [showFinding, setShowFinding] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["case", id],
		queryFn: () => api.get<CaseDetailData>(`/api/cases/${id}`),
		enabled: !!id,
	});
	const invalidate = () => {
		qc.invalidateQueries({ queryKey: ["case", id] });
		qc.invalidateQueries({ queryKey: ["cases"] });
	};
	const save = useMutation({
		mutationFn: (body: { notes?: string; status?: string; name?: string }) =>
			api.patch(`/api/cases/${id}`, body),
		onSuccess: () => {
			toast.addToast("success", "Saved");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	async function download(format: "json" | "csv") {
		try {
			const res = await fetch(`${API_BASE}/api/cases/${id}/export?format=${format}`, {
				credentials: "include",
			});
			if (!res.ok) throw new Error(`${res.status}`);
			const blob = await res.blob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `case-${id.slice(0, 8)}.${format}`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (e) {
			toast.addToast("error", `Export failed: ${String(e)}`);
		}
	}

	if (isLoading) return <Page title="Case">Loading…</Page>;
	if (!data) return <Page title="Case">Not found.</Page>;
	const c = data.case;
	const notesVal = notes ?? c.notes ?? "";

	return (
		<Page
			title={
				<span className="inline-flex items-center gap-2">
					{c.name} <Badge>{c.status}</Badge>
				</span>
			}
			description={
				<span>
					case {id.slice(0, 8)} · created {fmtDate(c.createdAt, false)}
					{c.partnerId && (
						<>
							{" · "}
							<Link to={`/partners/${c.partnerId}`} className="text-primary hover:underline">
								{c.partnerName ?? "partner"} →
							</Link>
						</>
					)}
				</span>
			}
			actions={
				<div className="flex flex-wrap gap-2 items-center">
					<div className="w-36">
						<Select
							value={c.status}
							onChange={(e) => save.mutate({ status: e.target.value })}
							options={STATUSES.map((s) => ({ value: s, label: s }))}
							size="sm"
						/>
					</div>
					<Button variant="outline" onClick={() => download("csv")}>
						Export CSV
					</Button>
					<Button variant="outline" onClick={() => download("json")}>
						Export JSON
					</Button>
				</div>
			}
		>
			<div className="grid gap-3.5" style={{ gridTemplateColumns: "2fr 1fr" }}>
				<div>
					<Card className="mb-3.5">
						<CardBody>
							<div className="flex items-center justify-between mb-2">
								<h2 className="text-sm font-semibold text-content">
									Findings ({data.findings.length})
								</h2>
								<Button size="sm" variant="secondary" onClick={() => setShowFinding(true)}>
									Add finding
								</Button>
							</div>
							{data.findings.map((f) => (
								<div key={f.id} className="border-b border-line last:border-0 py-2">
									<div className="flex items-center gap-2 text-sm">
										<Badge variant={severityTone(f.severity)}>{f.severity ?? "—"}</Badge>
										<Badge>{f.kind}</Badge>
										<span className="text-content font-medium">{f.summary}</span>
									</div>
									{f.detail && (
										<div className="text-xs text-muted mt-1 whitespace-pre-wrap">{f.detail}</div>
									)}
									<div className="text-[11px] text-muted mt-1">
										{fmtAgo(f.createdAt)}
										{f.createdBy ? ` · ${f.createdBy}` : ""}
									</div>
								</div>
							))}
							{!data.findings.length && <div className="text-xs text-muted">No findings yet.</div>}
						</CardBody>
					</Card>
					<Card>
						<CardBody>
							<h2 className="text-sm font-semibold text-content mb-2">Notes</h2>
							<Textarea
								value={notesVal}
								onChange={(e) => setNotes(e.target.value)}
								className="w-full min-h-[140px] text-sm"
							/>
							<div className="mt-2">
								<Button
									variant="primary"
									loading={save.isPending}
									onClick={() => save.mutate({ notes: notesVal })}
								>
									Save notes
								</Button>
							</div>
						</CardBody>
					</Card>
				</div>
				<Card>
					<CardBody>
						<div className="flex items-center justify-between mb-2">
							<h2 className="text-sm font-semibold text-content">Items ({data.items.length})</h2>
							<Button size="sm" variant="secondary" onClick={() => setShowItem(true)}>
								Add item
							</Button>
						</div>
						{data.items.map((it) => (
							<ItemRow key={it.id} item={it} />
						))}
						{!data.items.length && (
							<div className="text-xs text-muted">
								No items — add addresses from their detail page.
							</div>
						)}
					</CardBody>
				</Card>
			</div>

			<AddItemModal
				caseId={id}
				open={showItem}
				onClose={() => setShowItem(false)}
				onDone={invalidate}
			/>
			<AddFindingModal
				caseId={id}
				open={showFinding}
				onClose={() => setShowFinding(false)}
				onDone={invalidate}
			/>
		</Page>
	);
}

function ItemRow({ item }: { item: CaseItem }) {
	const href =
		item.itemType === "address"
			? `/addresses/${item.itemId}`
			: item.itemType === "partner"
				? `/partners/${item.itemId}`
				: item.itemType === "trace"
					? `/traces/${item.itemId}`
					: null;
	const text = item.label ?? item.itemId;
	return (
		<div className="text-sm mb-1.5">
			<Badge size="sm">{item.itemType}</Badge>{" "}
			{href ? (
				<Link to={href} className="text-primary hover:underline break-all font-mono text-xs">
					{text}
				</Link>
			) : (
				<span className="text-content break-all font-mono text-xs">{text}</span>
			)}
			{item.note && <div className="text-[11px] text-muted ml-1">{item.note}</div>}
		</div>
	);
}

function AddItemModal({
	caseId,
	open,
	onClose,
	onDone,
}: { caseId: string; open: boolean; onClose: () => void; onDone: () => void }) {
	const toast = useToast();
	const [itemType, setItemType] = useState("address");
	const [itemId, setItemId] = useState("");
	const [note, setNote] = useState("");
	const add = useMutation({
		mutationFn: () =>
			api.post(`/api/cases/${caseId}/items`, {
				itemType,
				itemId: itemId.trim(),
				note: note.trim() || undefined,
			}),
		onSuccess: () => {
			toast.addToast("success", "Item added");
			setItemId("");
			setNote("");
			onDone();
			onClose();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	return (
		<Modal
			open={open}
			onClose={onClose}
			title="Add item"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!itemId.trim()}
						loading={add.isPending}
						onClick={() => add.mutate()}
					>
						Add
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				<FormField label="Type" htmlFor="ci-type">
					<Select
						id="ci-type"
						value={itemType}
						onChange={(e) => setItemType(e.target.value)}
						options={ITEM_TYPES.map((t) => ({ value: t, label: t }))}
					/>
				</FormField>
				<FormField
					label="Id"
					htmlFor="ci-id"
					hint="uuid of the address / partner / transfer / trace / label"
				>
					<Input
						id="ci-id"
						value={itemId}
						onChange={(e) => setItemId(e.target.value)}
						className="font-mono"
					/>
				</FormField>
				<FormField label="Note" htmlFor="ci-note">
					<Input id="ci-note" value={note} onChange={(e) => setNote(e.target.value)} />
				</FormField>
			</div>
		</Modal>
	);
}

function AddFindingModal({
	caseId,
	open,
	onClose,
	onDone,
}: { caseId: string; open: boolean; onClose: () => void; onDone: () => void }) {
	const toast = useToast();
	const [kind, setKind] = useState("note");
	const [severity, setSeverity] = useState("medium");
	const [summary, setSummary] = useState("");
	const [detail, setDetail] = useState("");
	const add = useMutation({
		mutationFn: () =>
			api.post(`/api/cases/${caseId}/findings`, {
				kind,
				severity,
				summary: summary.trim(),
				detail: detail.trim() || undefined,
			}),
		onSuccess: () => {
			toast.addToast("success", "Finding added");
			setSummary("");
			setDetail("");
			onDone();
			onClose();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	return (
		<Modal
			open={open}
			onClose={onClose}
			title="Add finding"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!summary.trim()}
						loading={add.isPending}
						onClick={() => add.mutate()}
					>
						Add
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				<div className="grid grid-cols-2 gap-2">
					<FormField label="Kind" htmlFor="cf-kind">
						<Select
							id="cf-kind"
							value={kind}
							onChange={(e) => setKind(e.target.value)}
							options={FINDING_KINDS.map((k) => ({ value: k, label: k }))}
						/>
					</FormField>
					<FormField label="Severity" htmlFor="cf-sev">
						<Select
							id="cf-sev"
							value={severity}
							onChange={(e) => setSeverity(e.target.value)}
							options={SEVERITIES.map((s) => ({ value: s, label: s }))}
						/>
					</FormField>
				</div>
				<FormField label="Summary" htmlFor="cf-summary" required>
					<Input id="cf-summary" value={summary} onChange={(e) => setSummary(e.target.value)} />
				</FormField>
				<FormField label="Detail" htmlFor="cf-detail">
					<Textarea
						id="cf-detail"
						value={detail}
						onChange={(e) => setDetail(e.target.value)}
						className="min-h-[100px]"
					/>
				</FormField>
			</div>
		</Modal>
	);
}
